import { gqlClient } from '../../components/AppProvider';
import { AppDispatch, AppThunk } from '../../store';
import { PagingResultModelOfDocumentArchiveModel, GetDocumentArchivesInput, RequestResult, UpsertNoteInput } from '../../gql-types.generated';
import { queryDocumentArchiveList } from "../../gql/QueryDocumentArchiveList";
import { 
    decrementDocumentArchiveRequestsInFlight, 
    fetchError,  
    fetchDocumentArchiveByIdSuccess,
    fetchDocumentArchiveListSuccess,
    incrementDocumentArchiveRequestsInFlight, 
    captureDeleteDocumentArchivesStatus, 
    captureAbandonDocumentArchivesStatus,
    captureAcknowledgeDocumentArchivesStatus,
    captureResendEmailsStatus,
    captureResendDocumentStatus,
    captureGetDocumentHtmlFileStatus,
    captureGetDocumentAcknowledgementFileStatus,
    captureUpsertDocumentArchiveNoteStatus,
    captureDeleteDocumentArchiveNoteStatus,
    setCurrentPage,
    selectPageCursors
} from './DocumentArchivesSlice';
import { mutationMassDeleteEntity } from '../../gql/MutationMassDeleteEntity';
import { mutationExecuteEdiActionSvcAction } from '../../gql/MutationExecuteEdiActionSvcAction';
import { mutationUpsertDocumentArchiveNote } from '../../gql/MutationUpsertDocumentArchiveNote';
import { mutationDeleteEntity } from '../../gql/MutationDeleteEntity';
import { getErrorMessage } from '../../util/Common';
import { queryDocumentArchiveById } from '../../gql/QueryDocumentArchiveById';
import { mutationUpdateDocumentArchiveIsAbandoned } from '../../gql/MutationUpdateDocumentArchiveIsAbandoned';
import { mutationAcknowledgeDocumentArchives } from '../../gql/MutationAcknowledgeDocumentArchives';

    // Unified pagination action
    export const fetchDocumentArchiveList = (
        targetPage: number,
        filters?: GetDocumentArchivesInput
    ): AppThunk => async (dispatch: AppDispatch, getState): Promise<void> => {
        const state = getState();
        const pageCursors = selectPageCursors(state);
        
        // Set the current page first
        dispatch(setCurrentPage(targetPage));
        
        // Determine the cursor to use - cursor gets you TO the target page
        let cursor: string | undefined;
        if (targetPage === 1) {
            cursor = undefined; // First page uses no cursor
        } else {
            // For page N, we need the cursor from page N-1 that points to page N
            cursor = pageCursors[targetPage];
        }
        
        // Update filters with the cursor
        const updatedFilters = {
            ...filters,
            after: cursor
        };
        
        dispatch(incrementDocumentArchiveRequestsInFlight());
        try {
            const documentArchiveList = await queryDocumentArchiveList(gqlClient, updatedFilters);
            if (documentArchiveList) {
                dispatch(fetchDocumentArchiveListSuccess(documentArchiveList as PagingResultModelOfDocumentArchiveModel));
            }
            dispatch(decrementDocumentArchiveRequestsInFlight());
        } catch (e) {
            dispatch(fetchError(e as Error));
            dispatch(decrementDocumentArchiveRequestsInFlight());
        }
    };

    export const deleteDocumentArchives = (ids: string[]): AppThunk =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            const massDeleteStatus = await mutationMassDeleteEntity(gqlClient, {
                ids,
                entityName: "DocumentArchive"
            });
            if (massDeleteStatus) {
                if (massDeleteStatus.result === RequestResult.Fail) {
                    getErrorMessage(massDeleteStatus.message as string, massDeleteStatus.errors, true, true);
                }
                dispatch(captureDeleteDocumentArchivesStatus(massDeleteStatus));
            }
        } catch (e) {
            dispatch(fetchError(e as Error));
        }

    };

    // this is to abandon a list of document archives. 
    // if in the future, there is a desire to revert an abandoned record, another action
    // will be needed that passes a flag for false (flag defaulted to true on backend)
    export const abandonDocumentArchives = (ids: string[], note: string): AppThunk =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            const abandonStatus = await mutationUpdateDocumentArchiveIsAbandoned(gqlClient, {
                inputData: {
                    documentArchiveIds: ids,
                    note
                }
            });
            if (abandonStatus) {
                if (abandonStatus.result === RequestResult.Fail) {
                    getErrorMessage(abandonStatus.message as string, abandonStatus.errors, true, true);
                }
                dispatch(captureAbandonDocumentArchivesStatus(abandonStatus));
            }
        } catch (e) {
            dispatch(fetchError(e as Error));
        }

    };

    export const acknowledgeDocumentArchives = (ids: string[], note: string): AppThunk =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            const acknowledgeStatus = await mutationAcknowledgeDocumentArchives(gqlClient, {
                inputData: {
                    documentArchiveIds: ids,
                    note
                }
            });
            if (acknowledgeStatus) {
                if (acknowledgeStatus.result === RequestResult.Fail) {
                    getErrorMessage(acknowledgeStatus.message as string, acknowledgeStatus.errors, true, true);
                }
                dispatch(captureAcknowledgeDocumentArchivesStatus(acknowledgeStatus));
            }
        } catch (e) {
            dispatch(fetchError(e as Error));
        }

    };

    export const resendEmails = (documentArchiveIds: string[]): AppThunk =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            let actionArguments = `{DocumentArchiveIds: ${JSON.stringify(documentArchiveIds)}}`;
            const actionStatus = await mutationExecuteEdiActionSvcAction(gqlClient, {
                actionName: "SendMultipleNotificationsEmailAction",
                actionArguments: actionArguments
            });
            if (actionStatus) {
                if (actionStatus.result === RequestResult.Fail) {
                    getErrorMessage(actionStatus.message as string, actionStatus.errors, true, true);
                }
                dispatch(captureResendEmailsStatus(actionStatus));
            }
        } catch (e) {
            dispatch(fetchError(e as Error));
        }

    };

    export const resendDocument = (
        internalTransactionId?: string
    ): AppThunk => async (dispatch: AppDispatch): Promise<void> => {
        try {
            let actionArguments = `{InternalTransactionId: '${internalTransactionId}'}`;
            const actionStatus = await mutationExecuteEdiActionSvcAction(gqlClient, {
                actionName: "ResendDocumentAction",
                actionArguments: actionArguments
            });
            if (actionStatus) {
                if (actionStatus.result === RequestResult.Fail) {
                    getErrorMessage(actionStatus.message as string, actionStatus.errors, true, true);
                }
                dispatch(captureResendDocumentStatus(actionStatus));
            }
        } catch (e) {
            dispatch(fetchError(e as Error));
        }

    };

    export const getDocumentHtmlFile = (internalTransactionId?: string): AppThunk =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            let actionArguments = `{InternalTransactionId: ${JSON.stringify(internalTransactionId)}}`;
            const actionStatus = await mutationExecuteEdiActionSvcAction(gqlClient, {
                actionName: "GetHtmlFileAction",
                actionArguments: actionArguments
            });
            if (actionStatus) {
                if (actionStatus.result === RequestResult.Fail) {
                    getErrorMessage(actionStatus.message as string, actionStatus.errors, true, true);
                }
                dispatch(captureGetDocumentHtmlFileStatus(actionStatus));
            }
        } catch (e) {
            dispatch(fetchError(e as Error));
        }

    };

    export const getDocumentAcknowledgementFile = (internalTransactionId?: string): AppThunk =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            let actionArguments = `{InternalTransactionId: ${JSON.stringify(internalTransactionId)}}`;
            const actionStatus = await mutationExecuteEdiActionSvcAction(gqlClient, {
                actionName: "GetAcknowledgementFileAction",
                actionArguments: actionArguments
            });
            if (actionStatus) {
                if (actionStatus.result === RequestResult.Fail) {
                    getErrorMessage(actionStatus.message as string, actionStatus.errors, true, true);
                }
                dispatch(captureGetDocumentAcknowledgementFileStatus(actionStatus));
            }
        } catch (e) {
            dispatch(fetchError(e as Error));
        }

    };

    export const fetchDocumentArchiveById = (
        id: string
    ) => async (dispatch: AppDispatch): Promise<void> => {
        try {
            const documentArchive = await queryDocumentArchiveById(gqlClient, id);
            if (documentArchive) {
                dispatch(fetchDocumentArchiveByIdSuccess(documentArchive))
            }
        } catch (e) {
            dispatch(fetchError(e as Error));
        }
    };

    export const upsertDocumentArchiveNote = (
        upsertNoteData: UpsertNoteInput,
    ): AppThunk =>
        async (dispatch: AppDispatch): Promise<void> => {
            try {
                const upsertNoteStatus = await mutationUpsertDocumentArchiveNote(gqlClient, {
                    upsertNoteData
                });
                if (upsertNoteStatus) {
                    if (upsertNoteStatus.result === RequestResult.Fail) {
                        dispatch(fetchError({ message: upsertNoteStatus.message } as Error));
                        getErrorMessage(upsertNoteStatus.message as string, upsertNoteStatus.errors, true, true);
                    }
                    dispatch(captureUpsertDocumentArchiveNoteStatus(upsertNoteStatus));
                }
            } catch (e) {
                dispatch(fetchError(e as Error));
            }
        };

    export const deleteDocumentArchiveNote = (id: string): AppThunk =>
        async (dispatch: AppDispatch): Promise<void> => {
            try {
                const deleteNoteStatus = await mutationDeleteEntity(gqlClient, {
                    id,
                    entityName: "DocumentArchiveNote"
                });
                if (deleteNoteStatus) {
                    if (deleteNoteStatus.result === RequestResult.Fail) {
                        getErrorMessage(deleteNoteStatus.message as string, deleteNoteStatus.errors, true, true);
                    }
                    dispatch(captureDeleteDocumentArchiveNoteStatus(deleteNoteStatus));
                }
            } catch (e) {
                dispatch(fetchError(e as Error));
            }
        };
